import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Check, MoreHorizontal, Pencil, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../../../components/ui/avatar';
import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../../components/ui/tooltip';
import userRoles from '../../../../../constants/userRoles';
import apiService from '../../../../../services/api.service';
import dateFormatter from '../../../../../utils/dateFormatter';
import getUserLastLoginDate from '../../../../../utils/lastLoginDate';
import ConfirmChangesDialog from './ConfirmChangesDialog';

const ENGLISH_LEVELS = [
  { key: '0', display: 'Empty', value: '?' },
  { key: '1', display: 'Elementary', value: 'A1' },
  { key: '2', display: 'Pre-intermediate', value: 'A2' },
  { key: '3', display: 'Pre-intermediate+', value: 'A2+' },
  { key: '4', display: 'Intermediate', value: 'B1' },
  { key: '5', display: 'Intermediate+', value: 'B1+' },
  { key: '6', display: 'Upper-Intermediate', value: 'B2' },
  { key: '7', display: 'Advanced', value: 'C1' },
  { key: '8', display: 'Native', value: 'C2' },
];

function UserTableRow({ user, departments, onSaveSuccess, onError }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [editError, setEditError] = useState(null);

  // Original values (for comparison)
  const [originalValues, setOriginalValues] = useState({
    department: null,
    level: null,
    goal: null,
    isAdmin: false,
    englishLevel: null,
  });

  // Current editing values
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [currentGoal, setCurrentGoal] = useState(null);
  const [currentIsAdmin, setCurrentIsAdmin] = useState(false);
  const [currentEnglishLevel, setCurrentEnglishLevel] = useState(null);

  // Initialize values from user data
  useEffect(() => {
    if (departments?.length) {
      const foundDepartment = departments.find((d) => d._id === user.departmentId);
      const foundLevel = foundDepartment?.levels?.find((l) => l._id === user.level);
      const foundGoal = foundDepartment?.levels?.find((l) => l._id === user.goal);
      const isAdmin = user.roles?.includes(userRoles.ADMIN);
      const englishLevel = ENGLISH_LEVELS.find((l) => l.value === user.englishLevel);

      setCurrentDepartment(foundDepartment);
      setCurrentLevel(foundLevel);
      setCurrentGoal(foundGoal);
      setCurrentIsAdmin(isAdmin);
      setCurrentEnglishLevel(englishLevel);

      setOriginalValues({
        department: foundDepartment,
        level: foundLevel,
        goal: foundGoal,
        isAdmin,
        englishLevel,
      });
    }
  }, [departments, user]);

  // Get available levels for goal selection (must be >= current level position)
  const availableGoalLevels = useMemo(() => {
    if (!currentDepartment?.levels || !currentLevel) return [];
    return currentDepartment.levels.filter((l) => l.position >= currentLevel.position);
  }, [currentDepartment, currentLevel]);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return dateFormatter.format(new Date(dateString)).split(',')[0];
  };

  const handleEditClick = () => {
    if (user.isSentReview) {
      setEditError('Cannot edit user with pending review');
      setTimeout(() => setEditError(null), 3000);
      return;
    }
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    // Reset to original values
    setCurrentDepartment(originalValues.department);
    setCurrentLevel(originalValues.level);
    setCurrentGoal(originalValues.goal);
    setCurrentIsAdmin(originalValues.isAdmin);
    setCurrentEnglishLevel(originalValues.englishLevel);
    setIsEditing(false);
  };

  const handleDepartmentChange = (departmentId) => {
    const newDepartment = departments.find((d) => d._id === departmentId);
    if (newDepartment) {
      setCurrentDepartment(newDepartment);
      // Reset level and goal to first level of new department
      const firstLevel = newDepartment.levels[0];
      setCurrentLevel(firstLevel);
      setCurrentGoal(firstLevel);
    }
  };

  const handleLevelChange = (levelId) => {
    const newLevel = currentDepartment?.levels?.find((l) => l._id === levelId);
    if (newLevel) {
      setCurrentLevel(newLevel);
      // If current goal is below new level, update goal
      if (currentGoal && newLevel.position > currentGoal.position) {
        setCurrentGoal(newLevel);
      }
    }
  };

  const handleGoalChange = (levelId) => {
    const newGoal = currentDepartment?.levels?.find((l) => l._id === levelId);
    if (newGoal) {
      setCurrentGoal(newGoal);
    }
  };

  const handleEnglishLevelChange = (key) => {
    const newLevel = ENGLISH_LEVELS.find((l) => l.key === key);
    setCurrentEnglishLevel(newLevel);
  };

  const hasChanges = useMemo(() => {
    return (
      currentDepartment?._id !== originalValues.department?._id ||
      currentLevel?._id !== originalValues.level?._id ||
      currentGoal?._id !== originalValues.goal?._id ||
      currentIsAdmin !== originalValues.isAdmin ||
      currentEnglishLevel?.value !== originalValues.englishLevel?.value
    );
  }, [
    currentDepartment,
    currentLevel,
    currentGoal,
    currentIsAdmin,
    currentEnglishLevel,
    originalValues,
  ]);

  const getChanges = () => {
    const changes = [];
    if (currentDepartment?._id !== originalValues.department?._id) {
      changes.push({
        field: 'Department',
        from: originalValues.department?.title,
        to: currentDepartment?.title,
      });
    }
    if (currentLevel?._id !== originalValues.level?._id) {
      changes.push({
        field: 'Level',
        from: originalValues.level?.key,
        to: currentLevel?.key,
      });
    }
    if (currentGoal?._id !== originalValues.goal?._id) {
      changes.push({
        field: 'Goal',
        from: originalValues.goal?.key,
        to: currentGoal?.key,
      });
    }
    if (currentIsAdmin !== originalValues.isAdmin) {
      changes.push({
        field: 'Admin',
        from: String(originalValues.isAdmin),
        to: String(currentIsAdmin),
      });
    }
    if (currentEnglishLevel?.value !== originalValues.englishLevel?.value) {
      changes.push({
        field: 'English Level',
        from: originalValues.englishLevel?.value || '-',
        to: currentEnglishLevel?.value || '-',
      });
    }
    return changes;
  };

  const handleSaveClick = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleConfirmSave = async () => {
    try {
      setIsSaving(true);

      const body = {};
      if (currentDepartment?._id !== originalValues.department?._id) {
        body.departmentId = currentDepartment._id;
      }
      if (currentLevel?._id !== originalValues.level?._id) {
        body.level = currentLevel._id;
      }
      if (currentGoal?._id !== originalValues.goal?._id) {
        body.goal = currentGoal._id;
      }
      if (currentIsAdmin !== originalValues.isAdmin) {
        body.isAdmin = currentIsAdmin;
      }
      if (currentEnglishLevel) {
        body.englishLevel = currentEnglishLevel.value;
      }

      await apiService.admin.updateUser(user._id, body);

      // Update original values to current
      setOriginalValues({
        department: currentDepartment,
        level: currentLevel,
        goal: currentGoal,
        isAdmin: currentIsAdmin,
        englishLevel: currentEnglishLevel,
      });

      setShowConfirmDialog(false);
      setIsEditing(false);
      onSaveSuccess();
    } catch (error) {
      console.error('Failed to update user:', error);
      if (onError) {
        onError('Failed to update user');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      {editError && (
        <tr>
          <td colSpan={12} className="px-3 py-2">
            <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-red-600 text-sm">
              {editError}
            </div>
          </td>
        </tr>
      )}
      <tr className="border-b border-border hover:bg-muted/50 transition-colors">
        {/* Avatar */}
        <td className="px-3 py-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={user.hydraProfileLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.avatar?.small || user.avatar?.url} alt={user.email} />
                    <AvatarFallback className="text-xs">
                      {getInitials(user.firstName, user.lastName)}
                    </AvatarFallback>
                  </Avatar>
                </a>
              </TooltipTrigger>
              <TooltipContent>View Hydra Profile</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </td>

        {/* Last Name */}
        <td className="px-3 py-2 text-sm">{user.lastName || '-'}</td>

        {/* First Name */}
        <td className="px-3 py-2 text-sm">{user.firstName || '-'}</td>

        {/* Email */}
        <td className="px-3 py-2 text-sm text-muted-foreground">{user.email}</td>

        {/* Department */}
        <td className="px-3 py-2">
          {isEditing ? (
            <Select
              value={currentDepartment?._id || ''}
              displayValue={currentDepartment?.title || ''}
              onValueChange={handleDepartmentChange}
            >
              <SelectTrigger className="h-8 w-28">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((dept) => (
                  <SelectItem key={dept._id} value={dept._id}>
                    {dept.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm">{currentDepartment?.title || '-'}</span>
          )}
        </td>

        {/* Level */}
        <td className="px-3 py-2 text-center">
          {isEditing ? (
            <Select
              value={currentLevel?._id || ''}
              displayValue={currentLevel?.key || ''}
              onValueChange={handleLevelChange}
            >
              <SelectTrigger className="h-8 w-20">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {currentDepartment?.levels?.map((level) => (
                  <SelectItem key={level._id} value={level._id}>
                    {level.key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm font-medium">{currentLevel?.key || '-'}</span>
          )}
        </td>

        {/* Goal */}
        <td className="px-3 py-2 text-center">
          {isEditing ? (
            <Select
              value={currentGoal?._id || ''}
              displayValue={currentGoal?.key || ''}
              onValueChange={handleGoalChange}
            >
              <SelectTrigger className="h-8 w-20">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {availableGoalLevels.map((level) => (
                  <SelectItem key={level._id} value={level._id}>
                    {level.key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm font-medium">{currentGoal?.key || '-'}</span>
          )}
        </td>

        {/* Admin */}
        <td className="px-3 py-2 text-center">
          <Checkbox
            checked={currentIsAdmin}
            onCheckedChange={(checked) => setCurrentIsAdmin(checked)}
            disabled={!isEditing}
          />
        </td>

        {/* English Level */}
        <td className="px-3 py-2 text-center">
          {isEditing ? (
            <Select
              value={currentEnglishLevel?.key || ''}
              displayValue={currentEnglishLevel?.value || ''}
              onValueChange={handleEnglishLevelChange}
            >
              <SelectTrigger className="h-8 w-16">
                <SelectValue placeholder="-" />
              </SelectTrigger>
              <SelectContent>
                {ENGLISH_LEVELS.map((level) => (
                  <SelectItem key={level.key} value={level.key}>
                    {level.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm">{currentEnglishLevel?.value || '-'}</span>
          )}
        </td>

        {/* Created At */}
        <td className="px-3 py-2 text-sm text-muted-foreground">{formatDate(user.createdAt)}</td>

        {/* Last Login */}
        <td className="px-3 py-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-muted-foreground cursor-default">
                  {formatDate(user.lastLoginAt)}
                </span>
              </TooltipTrigger>
              <TooltipContent>{getUserLastLoginDate(user.lastLoginAt)}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </td>

        {/* Actions */}
        <td className="px-3 py-2 text-right">
          {isEditing ? (
            <div className="flex items-center justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                <X className="h-4 w-4" />
              </Button>
              <Button variant="default" size="sm" onClick={handleSaveClick} disabled={isSaving}>
                <Check className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleEditClick}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </td>
      </tr>

      <ConfirmChangesDialog
        isOpen={showConfirmDialog}
        changes={getChanges()}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelConfirm}
        isLoading={isSaving}
      />
    </>
  );
}

UserTableRow.propTypes = {
  user: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    departmentId: PropTypes.string,
    level: PropTypes.string,
    goal: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
    englishLevel: PropTypes.string,
    avatar: PropTypes.shape({
      small: PropTypes.string,
      url: PropTypes.string,
    }),
    hydraProfileLink: PropTypes.string,
    createdAt: PropTypes.string,
    lastLoginAt: PropTypes.string,
    isSentReview: PropTypes.bool,
  }).isRequired,
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      levels: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string.isRequired,
          key: PropTypes.string.isRequired,
          position: PropTypes.number.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

UserTableRow.defaultProps = {
  onError: undefined,
};

export default UserTableRow;
